import { DefaultExport, Params, Route, Routes, UrlSegment } from '@angular/router';
import { ObjectTools } from './object-tools';
import { BrowserTools } from './browser-tools';
import { Observable } from 'rxjs';
import {
  AuthDebugGuard,
  AuthGuard,
  AuthLogoutGuard,
  AuthProfileGuard,
  NavigationState,
} from '../services/navigation.service';
import { SolTab } from '../components/tab/sol-tabs/sol-tabs.component';
import {
  SolMenuGroup,
  SolMenuItem,
} from '../components/popover/sol-popover-menu-content/sol-popover-menu-content.state';
import { SolRequiredAuthorities } from '../services/authorization.service';
import { Type } from '@angular/core';
import {
  SolApp,
  SolBreadcrumb,
  SolNavbar,
  SolNavbarItem,
  SolRoute,
  SolRouteId,
  SolTopbarData,
} from '../models/sol-navigation';
import { SolText } from '../models/core/sol-text';

export enum NavigationTarget {
  SameTab = 'SameTab',
  NewTab = 'NewTab',
}

export interface UpdateQueryParamsOptions {
  mode: 'patch' | 'put';
  replaceUrl: boolean;
  preserveFragment: boolean;
}

export class RouterTools {
  /**
   * Redirect alias for a route that has sub-routes, pointing at the one to land on.
   *
   * `vueUrl` is where the same page lives in the Vue app, given rather than derived because the
   * two do not always spell it the same way.
   */
  public static ofRedirect(from: SolRouteId, to: SolRouteId, vueUrl?: string): Route {
    return {
      path: from,
      pathMatch: 'full',
      redirectTo: to,
      data: {
        id: from,
        vueUrl,
      },
    };
  }

  public static ofExternal(id: SolRouteId, externalUrl: string, authorities: SolRequiredAuthorities): Route {
    return {
      path: id,
      pathMatch: 'full',
      loadComponent: () =>
        import('../components/core/sol-error-page/sol-error-route-page.component').then(
          m => m.SolErrorRoutePageComponent,
        ),
      data: { id, externalUrl, authorities },
    };
  }

  public static ofLazy(
    id: SolRouteId,
    loadComponent: () =>
      | Type<unknown>
      | Observable<Type<unknown> | DefaultExport<Type<unknown>>>
      | Promise<Type<unknown> | DefaultExport<Type<unknown>>>,
    authorities: SolRequiredAuthorities = [],
    vueUrl?: string,
  ): Route {
    return {
      path: id,
      pathMatch: 'full',
      loadComponent: loadComponent,
      data: { id, authorities, vueUrl },
    };
  }

  /**
   * Route that only lives in the Vue app. `navigate()` delegates to `vueUrl` without an
   * explicit `toVueApp`, so callers never have to remember the flag. Direct URL visits
   * fall back to the generic error page (there is no Angular screen for these routes).
   */
  public static ofVue(id: SolRouteId, vueUrl: string, authorities: SolRequiredAuthorities = []): Route {
    return {
      path: id,
      pathMatch: 'full',
      loadComponent: () =>
        import('../components/core/sol-error-page/sol-error-route-page.component').then(
          m => m.SolErrorRoutePageComponent,
        ),
      data: { id, authorities, vueUrl, vueOnly: true },
    };
  }

  /**
   * Declares the app every route belongs to (`data.app`). Guards rerun on a query-only change too,
   * because the app context lives in the query.
   */
  public static ofApp(app: SolRouteId, routes: Routes): Routes {
    return routes.map(route => ({
      ...route,
      data: { ...route.data, app },
      runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    }));
  }

  public static ofProfile(id: SolRouteId): Route {
    return {
      path: id,
      canActivate: [AuthGuard, AuthProfileGuard],
      loadComponent: () =>
        import('../components/core/sol-error-page/sol-error-route-page.component').then(
          m => m.SolErrorRoutePageComponent,
        ),
      data: { id, external: false, authorities: [] },
    };
  }

  public static ofLogout(id: SolRouteId): Route {
    return {
      path: id,
      canActivate: [AuthGuard, AuthLogoutGuard],
      loadComponent: () =>
        import('../components/core/sol-error-page/sol-error-route-page.component').then(
          m => m.SolErrorRoutePageComponent,
        ),
      data: { id, authorities: [] },
    };
  }

  public static ofError(id: SolRouteId): Route {
    return {
      path: id,
      loadComponent: () =>
        import('../components/core/sol-error-page/sol-error-route-page.component').then(
          m => m.SolErrorRoutePageComponent,
        ),
      data: { id, showNavbar: false },
    };
  }

  public static ofDebug(id: SolRouteId, authorities: SolRequiredAuthorities = []): Route {
    return {
      path: id,
      canActivate: [AuthDebugGuard],
      loadComponent: () =>
        import('../components/core/sol-error-page/sol-error-route-page.component').then(
          m => m.SolErrorRoutePageComponent,
        ),
      data: { id, external: false, authorities },
    };
  }

  /**
   * Check whether a URL uses an allowed protocol (http or https).
   * Returns false for javascript:, data:, vbscript:, and other dangerous schemes.
   */
  public static isAllowedUrl(url: string): boolean {
    try {
      const parsed = new URL(url, window.location.origin);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  }

  /**
   * Navigate to the given URL.
   */
  public static goToExternal(url: string, options: NavigationTarget = NavigationTarget.SameTab): void {
    if (!RouterTools.isAllowedUrl(url)) {
      console.warn('Blocked navigation to disallowed URL:', url);
      return;
    }
    if (options === NavigationTarget.SameTab) {
      window.location.href = url;
    } else if (options === NavigationTarget.NewTab) {
      window.open(url, '_blank');
    } else {
      throw new Error(`Unknown option ${options}`);
    }
  }

  /**
   * Download a file from the given URL.
   */
  public static downloadFile(url: string): void {
    if (!RouterTools.isAllowedUrl(url)) {
      console.warn('Blocked download from disallowed URL:', url);
      return;
    }
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.download = ''; // or a filename: "report.pdf"
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  public static findRoute(state: NavigationState, routeId?: SolRouteId): SolRoute | undefined {
    for (const route of state.routes) {
      if (route.id === routeId) {
        return route;
      }
    }
    return undefined;
  }

  public static computeNavbarItems(state: NavigationState, items: SolNavbarItem[]): SolNavbarItem[] {
    return items.map(item => this.computeNavbarItem(state, item, true, false));
  }

  public static computeNavbarItem(
    state: NavigationState,
    item: SolNavbarItem,
    parentShowChildrenInNavbar: boolean,
    parentShowChildrenAsTabs: boolean,
  ): SolNavbarItem {
    // Route
    const route = this.findRoute(state, item.id);
    const showChildrenAsTabs = item.showChildrenAsTabs;
    const showChildrenInNavbar = item.showChildrenInNavbar && parentShowChildrenInNavbar && !parentShowChildrenAsTabs;
    const isMenuGroup = item.children.length > 0 && showChildrenInNavbar && !showChildrenAsTabs;
    const children = item.children.map(child =>
      this.computeNavbarItem(state, child, showChildrenInNavbar, showChildrenAsTabs),
    );
    const childHasAuthority = children.some(child => child.hasAuthority);
    const routeHasAccess = route !== undefined ? route.hasAuthority : false;
    const isContainer = route === undefined || route.isRedirect;
    const hasAuthority = isContainer && item.children.length > 0 ? childHasAuthority : routeHasAccess;
    return item.withComputedAuthorities(hasAuthority, showChildrenInNavbar, showChildrenAsTabs, isMenuGroup, children);
  }

  public static hasAuthorityByRouteId(state: NavigationState, routeId: SolRouteId): boolean {
    const items = [...state.navbar.menu, ...state.navbar.system, ...state.navbar.account];
    return this.hasAuthorityForNavbarItems(items, routeId) || false;
  }

  public static hasAuthorityForNavbarItems(items: SolNavbarItem[], routeId: SolRouteId): boolean | undefined {
    for (const item of items) {
      if (item.id === routeId) {
        return item.hasAuthority;
      }

      const hasChildAuthority = this.hasAuthorityForNavbarItems(item.children, routeId);
      if (hasChildAuthority !== undefined) {
        return hasChildAuthority;
      }
    }
    return undefined;
  }

  /**
   * Replace only full-segment placeholders (e.g. "/:id" or "/:id/"),
   * and only when the key exists in pathParams. Otherwise, keep them.
   */
  public static calculateInternalUrl(path: string, pathParams: Params = {}): string {
    return path.replace(/\/:(\w+)(?=\/|$)/g, (full, key) => {
      const val = pathParams[key];
      return val != null ? `/${String(val)}` : full;
    });
  }

  /**
   * Replace any placeholder keys (variables key) with their respective values (variables value).
   * For example:
   * - path: `{{URL}}/some-path`
   * - variables: {"URL": "https://example.com" }
   * - Result: `https://example.com/some-path`
   */
  public static calculateExternalUrl(path: string, variables: Record<string, string>): string {
    return path.replace(/{{(.*?)}}/g, (_, key) => variables[key] || '');
  }

  public static urlSegmentsToUrl(url: UrlSegment[]): string {
    return url.map(segment => segment.path).join('/');
  }

  /**
   * Calculates the data needed to populate the navigation content of the topbar.
   */
  public static toTopbarData(nss: NavigationState): SolTopbarData {
    // Tabs
    const routeId = nss.activeRouteId;
    const parentItem = RouterTools.findTopbarParent(nss, routeId);
    // In embedded mode a node with embedTabGroup: true acts as a tab-group root even
    // when the navbar is hidden. This parallels showChildrenAsTabs for embedded scenarios.
    const showInTopbar =
      (parentItem?.showChildrenAsTabs ?? false) || (BrowserTools.isEmbedded() && (parentItem?.embedTabGroup ?? false));
    const children = showInTopbar ? (parentItem?.children ?? []) : [];
    const tags = nss.navbarItemTags;
    const hidden = nss.navbarItemHidden;
    const tabs: SolTab[] = children
      .map(item => RouterTools.findNavbarItem(nss.navbar, item.id))
      .filter(item => item?.hasAuthority ?? false)
      .filter(item => !hidden[item?.id ?? ''])
      .map(item => ({
        id: item?.id || '',
        name: item?.displayName || '',
        tag: tags[item?.id ?? ''],
      }));
    const selectedTab = routeId ?? '';

    // page name
    const breadcrumbItems = RouterTools.findBreadcrumbs(nss.navbar, routeId);
    const pageNameParams = nss.pageNameParams;
    const breadcrumbs: SolBreadcrumb[] = breadcrumbItems.map((item, index) => ({
      id: item.id,
      name: SolText.of(item.displayName ?? '', pageNameParams),
      icon: item.icon,
      clickable: index < breadcrumbItems.length - 1,
      embedTabGroup: item.embedTabGroup ?? false,
    }));
    const pageNameParamsRequired = breadcrumbItems.flatMap(item => item.nameParams);
    const pageNamesLoaded = pageNameParamsRequired.every(key => key in pageNameParams);
    return { breadcrumbs, pageNamesLoaded, tabs, selectedTab };
  }

  /**
   * Find the part of the navbar tree that should be shown as breadcrumbs. Examples:
   *
   * Example 1:
   * - Technologies (default)
   * - Vulnerability Catalog (showChildrenAsTabs: true)
   * - Dashboard (default) (=routeId)
   * Result: [Vulnerability Catalog]
   *
   * Example 2:
   * - Administration (default)
   * - Scopes (showChildrenInNavbar: false)
   * - Scope (showChildrenAsTabs: true)
   * - Edit scope (default) (=routeId)
   * Result: [Scopes, Scope]
   *
   * Example 3:
   * - Downloads (default) (=routeId)
   * Result: [Downloads]
   *
   * Example 4:
   * - DRP (default)
   * - Credentials (default) (=routeId)
   * Result: [Credentials]
   *
   * Example 5:
   * - Usage (default)
   * - Licensing (showChildrenAsTabs: true)
   * - Licenses (default)
   * - Edit license (default) (=routeId)
   * Result: [Licenses, Edit license]
   */
  public static findBreadcrumbs(navbar: SolNavbar, routeId: SolRouteId | undefined): SolNavbarItem[] {
    // Nothing found
    const activeItems = RouterTools.findActiveNavbarItems(navbar, routeId);
    if (activeItems.length === 0) {
      return [];
    }

    // Determine where to start (first hidden child or tab container, otherwise active leaf)
    let startIndex = activeItems.findIndex(item => !item.showChildrenInNavbar || item.showChildrenAsTabs);
    if (startIndex === -1) {
      startIndex = activeItems.length - 1;
    }

    // Find the last tab "page" (an item whose parent shows children as tabs)
    let lastTabPageIndex = -1;
    for (let i = activeItems.length - 1; i > 0; i--) {
      if (activeItems[i - 1].showChildrenAsTabs) {
        lastTabPageIndex = i;
        break;
      }
    }

    // Determine where to stop:
    // - If the active route itself is a tab page (lastTabPageIndex is the last item), stop before it.
    // - If the active route is deeper inside a tab page, stop at that tab page (drop deeper content).
    // - Otherwise include everything down to the active item.
    let endIndex: number;
    if (lastTabPageIndex !== -1) {
      const activeIndex = activeItems.length - 1;
      const tabContainerCount = activeItems.filter(item => item.showChildrenAsTabs).length;
      if (activeIndex === lastTabPageIndex) {
        endIndex = lastTabPageIndex; // active item is a tab page -> omit it
      } else if (activeIndex === lastTabPageIndex + 1 && tabContainerCount <= 1) {
        endIndex = activeIndex + 1; // inside single tab container -> include next item
      } else {
        endIndex = lastTabPageIndex + 1; // stop at the tab page
      }
    } else {
      endIndex = activeItems.length;
    }

    // Ensure we always return at least one item
    if (endIndex <= startIndex) {
      endIndex = startIndex + 1;
    }

    return activeItems.slice(startIndex, endIndex);
  }

  public static findNavbarItem(navbar: SolNavbar, routeId: SolRouteId): SolNavbarItem | undefined {
    const allItems = RouterTools.toAllNavbarItems(navbar);
    return ObjectTools.findLast(RouterTools.findNavbarItemChain(allItems, routeId));
  }

  public static findActiveNavbarItems(navbar: SolNavbar, routeId: SolRouteId | undefined): SolNavbarItem[] {
    const allItems = RouterTools.toAllNavbarItems(navbar);
    return RouterTools.findNavbarItemChain(allItems, routeId);
  }

  public static toAllNavbarItems(navbar: SolNavbar): SolNavbarItem[] {
    return [...navbar.menu, ...navbar.system, ...navbar.account];
  }

  /**
   * Find which app owns the route: the one it declares (`data.app`), else by navbar. Routes can exist in
   * multiple navbars, so if that happens:
   * - Check if the route is found in the navbar of the current app
   * - If not, check if it exists in the navbar of any other app, in order (default app)
   * - If not, return undefined, which leaves the route ungated by any app
   *
   * Only an app's own menu and system items confer ownership. The account section is chrome every app
   * repeats, so a profile or preferences entry never ties those routes to one app's gating.
   */
  public static findApp(nss: NavigationState, routeId: SolRouteId): SolApp | undefined {
    const declared = RouterTools.findRoute(nss, routeId)?.app;
    if (declared !== undefined) {
      return nss.apps.find(app => app.id === declared);
    }

    // Route found in current app?
    const app = RouterTools.findCurrentApp(nss);
    if (app?.navbar) {
      const item = RouterTools.findOwnedNavbarItem(app.navbar, routeId);
      if (item) {
        return app;
      }
    }

    // Route found in other apps
    for (const app of nss.apps) {
      if (app.id === nss.appId) {
        continue; // Skip current (already checked)
      }
      const navbar = app.navbar;
      const item = !navbar ? undefined : RouterTools.findOwnedNavbarItem(navbar, routeId);
      if (item) {
        return app;
      }
    }

    // Nothing found
    return undefined;
  }

  public static findCurrentApp(nss: NavigationState): SolApp | undefined {
    return nss.appId === undefined ? undefined : nss.apps.find(app => app.id === nss.appId);
  }

  /** The app whose chrome a route no app owns is shown in: the first one the caller can actually open. */
  public static findAccessibleApp(nss: NavigationState): SolApp | undefined {
    return nss.apps.find(app => app.hasAuthority && app.navbar !== undefined);
  }

  public static computeAppMenu(nss: NavigationState): SolMenuGroup[] {
    // Filter apps
    const apps = nss.apps.filter(app => app.hasAuthority);

    // Create menu
    let curGroup: string | undefined = undefined;
    let curItems: SolMenuItem[] = [];
    const result: SolMenuGroup[] = [];
    for (let t = 0; t < apps.length; t++) {
      const app = apps[t];
      if (curGroup === undefined || app.group !== curGroup) {
        if (!!curGroup) {
          result.push({
            name: curGroup,
            items: curItems,
          });
        }
        curGroup = app.group;
        curItems = [];
      }
      curItems.push({
        id: app.id,
        name: app.name,
        icon: app.icon,
        selected: app.id === nss.appId,
      });
    }

    // Add remaining items (only give group name if there are multiple groups)
    if (curItems.length > 0) {
      result.push({
        name: result.length === 0 ? undefined : curGroup,
        items: curItems,
      });
    }
    return result;
  }

  /**
   * Strip query params and replace dynamic path segments with their route-template placeholders.
   * E.g. "https://app.example.com/domains/abc123?tab=x" with routeId "domains/:id"
   *   → "https://app.example.com/domains/:id"
   */
  public static sanitizeUrl(url: string, routeId: SolRouteId | undefined): string {
    let sanitized = url.replace(/\?.*$/, '');
    if (routeId?.includes(':')) {
      const pattern = routeId.replace(/:(\w+)/g, '[^/]+');
      sanitized = sanitized.replace(new RegExp(pattern), routeId);
    }
    return sanitized;
  }

  private static findOwnedNavbarItem(navbar: SolNavbar, routeId: SolRouteId): SolNavbarItem | undefined {
    const owned = [...navbar.menu, ...navbar.system];
    return ObjectTools.findLast(RouterTools.findNavbarItemChain(owned, routeId));
  }

  private static findNavbarItemChain(
    items: SolNavbarItem[],
    routeId: SolRouteId | undefined,
    chain: SolNavbarItem[] = [],
  ): SolNavbarItem[] {
    for (const item of items) {
      // Found match
      const newChain = [...chain, item];
      if (item.id === routeId) {
        return newChain;
      }

      // Search children
      const candidateChain = RouterTools.findNavbarItemChain(item.children, routeId, newChain);
      if (candidateChain.length > 0) {
        return candidateChain;
      }
    }
    return [];
  }

  private static findTopbarParent(nss: NavigationState, routeId: SolRouteId | undefined): SolNavbarItem | undefined {
    const allItems = [...nss.navbar.menu, ...nss.navbar.system, ...nss.navbar.account];
    return RouterTools.findParentItem(allItems, routeId);
  }

  private static findParentItem(items: SolNavbarItem[], routeId: SolRouteId | undefined): SolNavbarItem | undefined {
    for (const item of items) {
      const found = !!item.children.find(child => child.id === routeId);
      if (found) {
        return item;
      }

      const foundNested = RouterTools.findParentItem(item.children, routeId);
      if (foundNested) {
        return foundNested;
      }
    }
    return undefined;
  }
}
